import { useState } from "react";
import axios from "axios";
import { ArrowRight, Download, CheckCircle2 } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/scroll";
import { useLang } from "@/i18n";

const FreeGuide = () => {
  const { t } = useLang();
  const g = t.guide;
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/subscribers`, {
        email,
        source: "free-guide",
        website: honeypot,
      });
      setDone(true);
    } catch (err) {
      setError(g.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="free-guide"
      data-testid="free-guide-section"
      className="py-24 md:py-32 bg-[#0A0A0A] border-y border-white/10"
    >
      <div className="container-x">
        <Chapter num="04" label={g.chapter} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl lg:text-7xl uppercase tracking-tight leading-[0.9]">
              {g.titleA} <span className="text-[#D91624]">{g.titleB}</span>{" "}
              {g.titleC}
            </h2>
            <p className="mt-6 max-w-xl text-base md:text-lg text-neutral-400 leading-relaxed">
              {g.intro}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            {done ? (
              <div
                data-testid="free-guide-success"
                className="border border-[#D4AF37]/40 bg-[#121212] p-8 md:p-10"
              >
                <CheckCircle2 size={40} className="text-[#D4AF37]" />
                <h3 className="font-display text-3xl md:text-4xl uppercase mt-4">
                  {g.successTitle}
                </h3>
                <p className="mt-3 text-neutral-400">
                  {g.successBody}{" "}
                  <span className="text-white font-semibold">{email}</span>.{" "}
                  {g.successNote}
                </p>
                <a
                  data-testid="free-guide-download-btn"
                  href={`${API}/guides/fitbersama-3-mistakes.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline mt-6"
                >
                  <Download size={16} /> {g.download}
                </a>
              </div>
            ) : (
              <form
                data-testid="free-guide-form"
                onSubmit={submit}
                className="border border-white/10 bg-[#121212] p-8 md:p-10"
              >
                <label
                  htmlFor="guide-email"
                  className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold"
                >
                  {g.label}
                </label>
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <div className="mt-3 flex flex-col sm:flex-row gap-3">
                  <Input
                    id="guide-email"
                    data-testid="free-guide-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={g.placeholder}
                    className="h-14 bg-neutral-900 border-white/10 rounded-none text-white placeholder:text-neutral-600 focus-visible:ring-[#D4AF37]"
                  />
                  <button
                    data-testid="free-guide-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-primary whitespace-nowrap disabled:opacity-50"
                  >
                    {loading ? g.sending : g.submit} <ArrowRight size={16} />
                  </button>
                </div>
                {error && (
                  <p data-testid="free-guide-error" className="mt-3 text-sm text-[#D91624]">
                    {error}
                  </p>
                )}
                <p className="mt-4 text-xs text-neutral-600">{g.noSpam}</p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default FreeGuide;
